/**
 * Input — a labelled text field with error support.
 *
 * BUILT FOR React Hook Form (arriving in Milestone 5). It uses `forwardRef`
 * because form libraries need a direct reference to the underlying <input> to
 * read its value without re-rendering the whole form on every keystroke —
 * which is what keeps typing fast.
 *
 * ACCESSIBILITY is wired in, not bolted on: the label is tied to the input via
 * htmlFor/id, and errors are announced to screen readers via aria-describedby.
 */

import { forwardRef, useId } from 'react';
import { cn } from '@/lib/cn';

export const Input = forwardRef(function Input(
  { label, error, hint, leftIcon, className, id, ...props },
  ref,
) {
  // useId generates a stable unique id, so multiple inputs on one page never
  // collide — essential for the label/input association to work.
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-ink">
          {label}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
            {leftIcon}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'h-11 w-full rounded-lg border bg-white text-sm text-ink transition-colors',
            'placeholder:text-muted',
            'focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none',
            leftIcon ? 'pl-11 pr-3.5' : 'px-3.5',
            error ? 'border-danger' : 'border-line',
            className,
          )}
          {...props}
        />
      </div>

      {/* Error takes priority over hint — never show both. */}
      {error ? (
        <p id={errorId} className="mt-1.5 text-xs font-medium text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
});

export default Input;
